(() => {
  // Wait for Elevate assignment; control does not register gallery or popup listeners.
  const enabled = () => document.documentElement.getAttribute('data-mc-collection-quick-shop') === 'variant';
  let initialized = false;
  const initialize = () => {
    if (initialized || !enabled()) return;
    initialized = true;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  class CollectionCardGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const track = this.querySelector('.collection-card-gallery__track');
      const dots = [...this.querySelectorAll('[data-collection-image]')];
      const slides = [...track.children];
      const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      const index = () => Math.max(0, Math.min(slides.length - 1, Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1))));
      const go = (next) => track.scrollTo({ left: direction * Math.max(0, Math.min(slides.length - 1, next)) * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      const update = () => {
        const active = index();
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === active)));
        slides.forEach((slide, i) => { slide.tabIndex = i === active ? 0 : -1; });
      };
      let pointer;
      let suppressClickUntil = 0;
      track.addEventListener('scroll', update, { passive: true, signal });
      this.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-collection-image]');
        if (dot) { event.preventDefault(); go(Number(dot.dataset.collectionImage)); }
        else if (track.contains(event.target) && Date.now() < suppressClickUntil) event.preventDefault();
      }, { signal });
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index() + (event.key === 'ArrowRight' ? direction : -direction));
      }, { signal });
      track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      track.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        pointer = { id: event.pointerId, type: event.pointerType, x: event.clientX, scroll: track.scrollLeft, dragged: false };
      }, { signal });
      track.addEventListener('pointermove', (event) => {
        if (!pointer || pointer.id !== event.pointerId || pointer.type !== 'mouse') return;
        const delta = event.clientX - pointer.x;
        if (Math.abs(delta) > 4) {
          pointer.dragged = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }
        if (!pointer.dragged) return;
        event.preventDefault();
        track.scrollLeft = pointer.scroll - delta;
      }, { signal });
      const finish = (event) => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const moved = pointer.dragged || Math.abs(event.clientX - pointer.x) > 8 || Math.abs(track.scrollLeft - pointer.scroll) > 8;
        if (moved) suppressClickUntil = Date.now() + 400;
        if (track.hasPointerCapture(pointer.id)) track.releasePointerCapture(pointer.id);
        track.classList.remove('is-dragging');
        if (pointer.type === 'mouse' && moved) go(index());
        pointer = null;
      };
      track.addEventListener('pointerup', finish, { signal });
      track.addEventListener('pointercancel', finish, { signal });
      update();
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('collection-card-gallery')) customElements.define('collection-card-gallery', CollectionCardGallery);

  const dialog = document.getElementById('CollectionQuickShop');
  if (!dialog) return;
  const host = dialog.querySelector('[data-quick-shop-content]');
  const status = dialog.querySelector('[data-quick-shop-status]');
  const link = dialog.querySelector('[data-quick-shop-link]');
  const cache = new Map();
  let section;
  let trigger;
  let request;
  let closing;
  const unload = () => {
    section?.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true }));
    host.replaceChildren();
    section = null;
  };
  const close = () => {
    if (!dialog.open || closing) return;
    request?.abort();
    dialog.classList.add('is-closing');
    closing = window.setTimeout(() => {
      dialog.close();
      dialog.classList.remove('is-closing');
      document.documentElement.classList.remove('collection-quick-shop-open');
      unload();
      closing = null;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    }, reducedMotion() ? 0 : 350);
  };
  const activateScripts = (scope) => {
    scope.querySelectorAll('script').forEach((oldScript) => {
      const type = oldScript.getAttribute('type');
      if (type && type !== 'text/javascript' && type !== 'module') return;
      if (oldScript.textContent.includes('trigger-ajax-button') || oldScript.textContent.includes('document.body.appendChild(stickyAtc)')) return;
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  };
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-collection-quick-shop]');
    if (!enabled() || !button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(button.href, location.href);
    if (url.origin !== location.origin || dialog.open) return;
    event.preventDefault();
    trigger = button;
    link.href = url.href;
    dialog.querySelector('#CollectionQuickShopTitle').textContent = button.dataset.productTitle;
    status.textContent = status.dataset.loading;
    status.hidden = false;
    dialog.showModal();
    document.documentElement.classList.add('collection-quick-shop-open');
    dialog.querySelector('[data-quick-shop-close]').focus({ preventScroll: true });
    request?.abort();
    const activeRequest = request = new AbortController();
    try {
      let html = cache.get(url.href);
      if (!html) {
        const response = await fetch(url.href, { signal: activeRequest.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(status.dataset.error);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const hero = parsed.querySelector('[data-mphp-root], product-rerender[id^="product-info-"], [data-section-type="product"]');
        const productSection = hero?.closest('[id^="shopify-section-"], .shopify-section');
        if (!productSection) throw new Error(status.dataset.error);
        // Kaching's product and offer JSON live in the page head, outside the hero.
        // Keep that product context with the imported form before reinitializing the provider.
        const bundleData = parsed.querySelectorAll('script.kaching-bundles-product[type="application/json"], script.kaching-bundles-deal-block-settings[type="application/json"]');
        bundleData.forEach(data => productSection.prepend(data.cloneNode(true)));
        html = productSection.outerHTML;
        cache.set(url.href, html);
      }
      if (activeRequest.signal.aborted || !dialog.open) return;
      unload();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      section = document.importNode(parsed.body.firstElementChild, true);
      section.querySelectorAll('.new-sticky-atc, .calm-sticky-atc, .mphp__sticky-bar, .mphp__mobile-sticky').forEach(element => element.remove());
      host.replaceChildren(section);
      host.scrollTop = 0;
      window.kachingBundlesInitialize?.();
      activateScripts(section);
      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      status.hidden = true;
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = status.dataset.error;
    }
  });
  dialog.querySelector('[data-quick-shop-close]').addEventListener('click', close);
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('variant:add', (event) => {
    if (!event.detail || closing) return;
    const detail = { ...event.detail };
    event.detail.blockCartDrawerOpening = true;
    close();
    // The original form updates the cart normally. Open it after this modal closes.
    window.setTimeout(() => document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail })), reducedMotion() ? 0 : 360);
  }, true);
  };
  const assignmentObserver = new MutationObserver(initialize);
  assignmentObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mc-collection-quick-shop'] });
  initialize();
})();
